/* eslint-disable @typescript-eslint/ban-types */
import { lighten } from '@mui/material';
import { PaletteColor } from '@mui/material/styles';
import { CreateCSSProperties, createStyles, makeStyles } from '@mui/styles';
import { LoadingDoneVariant } from 'contexts';

import { LOADING_BAR_HEIGHT, LOADING_BAR_STATUS_SECONDS } from './constants';
import { LoadingBarProps } from './types';

const height = LOADING_BAR_HEIGHT;
const statusSeconds = LOADING_BAR_STATUS_SECONDS;

type LoadingBarVariant<TName extends LoadingDoneVariant> = Record<
  `status_${TName}`,
  CreateCSSProperties<{}>
> &
  Record<`wrapper_${TName}`, CreateCSSProperties<{}>> &
  Record<`@keyframes ${TName}`, CreateCSSProperties<{}>>;

const buildVariant = <TName extends LoadingDoneVariant>(
  color: PaletteColor,
  name: LoadingDoneVariant
) => {
  return {
    [`status_${name}`]: {
      animation: `$${name} ${statusSeconds}s forwards, $expand ${statusSeconds}s forwards`,
    },
    [`wrapper_${name}`]: {
      borderBottomColor: color.dark,
    },
    [`@keyframes ${name}`]: {
      '0%': {
        backgroundColor: color.main,
      },
      '50%': {
        backgroundColor: color.main,
      },
      '100%': {
        backgroundColor: color.light,
      },
    },
  } as LoadingBarVariant<TName>;
};

const useLoadingBarStyles = makeStyles(({ palette }) => {
  return createStyles({
    absoluteWrapper: {
      position: 'relative',
      width: '100%',
    },
    root: {
      width: '100%',
      position: (props: LoadingBarProps) => (props.absolute ? 'absolute' : 'relative'),
      zIndex: 99,
    },
    progressRoot: {
      backgroundColor: lighten(palette.info.light, 0.5),
      height,
    },
    bar: {
      backgroundColor: palette.info.dark,
    },
    statusWrapper: {
      width: '100%',
      position: 'absolute',
      height,
      borderBottomWidth: 1,
      borderBottomStyle: 'solid',
      zIndex: 4,
    },
    statusBar: {
      width: '15%',
      height,
      position: 'absolute',
      zIndex: 3,
    },
    '@keyframes expand': {
      '0%': {
        width: '10%',
      },
      '70%': {
        width: '100%',
      },
      '100%': {
        width: '100%',
      },
    },
    ...buildVariant<'error'>(palette.error, 'error'),
    ...buildVariant<'warning'>(palette.warning, 'warning'),
    ...buildVariant<'success'>(palette.success, 'success'),
  });
});

export default useLoadingBarStyles;
